import { useMutation } from '@apollo/client/react';
import { CardImageDetailsFragmentDoc } from '@graphql/fragments/cards/images.graphql';
import {
  CreateCardImageDocument,
  RemoveCardImageDocument,
  UpdateCardImageDocument,
} from '@graphql/mutations/cards/images.graphql';
import { useCardAttributes } from '@hooks/use-card-attributes';
import { logger } from '@modules/logger';
import { cardClaimContext } from 'app-next/claim/card/contexts';
import { useContext } from 'react';

import type {
  CardAttributeType,
  CardImage,
  CardImages,
  CreateCardImageInput,
  UpdateCardImageInput,
} from '.cache/__types__';

type CardImageType = keyof CardImages;
type CardImageInput = Partial<CardImage> & { card_id: string };

export function useCardImageMutations() {
  const { cardAttributeTypes } = useCardAttributes();

  const { claimCardToken } = useContext(cardClaimContext);

  const [createImage] = useMutation(CreateCardImageDocument);
  const [updateImage] = useMutation(UpdateCardImageDocument);
  const [removeImage] = useMutation(RemoveCardImageDocument);

  const mutationContext = claimCardToken
    ? {
        headers: {
          authorization: `ClaimCard ${claimCardToken}`,
        },
      }
    : undefined;

  const getAttributeId = (type: keyof CardImages) => {
    const attributeId = cardAttributeTypes.find((attr) => attr.name === type)?.id;

    if (!attributeId) {
      logger.warn(`Could not find attribute id for ${type}`);
      throw new Error(`Could not find attribute id for ${type}`);
    }

    return attributeId;
  };

  const create = async (input: CreateCardImageInput, type: CardImageType) => {
    const { data } = await createImage({
      variables: { input },
      context: mutationContext,
      optimisticResponse: {
        createCardImage: {
          image: {
            ...input,
            instruction_text: input.instruction_text ?? null,
            is_locked: input.is_locked ?? null,
            is_optional: input.is_optional ?? null,
            id: 'temp-id',
            __typename: 'CardImage',
            template_id: null,
            attribute_type: null,
            metadata: {
              ...input.metadata,
              editor_version: input.metadata?.editor_version ?? null,
            },
          },
          __typename: 'CreateCardImagePayload',
        },
      },
      update: (cache, { data }) => {
        const newImage = data?.createCardImage?.image;
        if (!newImage || !input.card_id) {
          return;
        }

        const cachedCardId = cache.identify({
          id: input?.card_id,
          __typename: 'Card',
        });

        const newCachedImage = cache.writeFragment({
          id: `CardImage:${newImage.id}`,
          fragment: CardImageDetailsFragmentDoc,
          fragmentName: 'CardImageDetails',
          data: newImage,
        });

        cache.modify({
          id: cachedCardId,
          fields: {
            images(existingImages = {}) {
              return { ...existingImages, [type]: newCachedImage };
            },
          },
        });
      },
    });

    return data?.createCardImage?.image;
  };

  const update = async (input: UpdateCardImageInput, attributeType?: CardAttributeType) => {
    // Pick only fields accepted by UpdateCardImageInput to prevent extra fields
    // (e.g. attribute_type from CardImage) from being sent to the mutation
    const sanitizedInput: UpdateCardImageInput = {
      id: input.id,
      card_id: input.card_id,
      original_url: input.original_url,
      edited_url: input.edited_url,
      is_locked: input.is_locked,
      is_optional: input.is_optional,
      is_profile_linked: input.is_profile_linked,
      instruction_text: input.instruction_text,
      metadata: input.metadata,
      stage: input.stage,
      template_id: input.template_id,
      template_status: input.template_status,
      value_provider: input.value_provider,
    };
    const { data } = await updateImage({
      variables: { input: sanitizedInput },
      context: mutationContext,
      optimisticResponse: {
        updateCardImage: {
          image: {
            __typename: 'CardImage',
            // Set defaults for fields that are not always provided by `input` so the optimistic response is always complete
            is_optional: null,
            instruction_text: null,
            is_locked: null,
            value_provider: null,
            stage: null,
            template_id: null,
            ...sanitizedInput,
            edited_url: sanitizedInput.edited_url ?? null,
            attribute_type: attributeType ?? null,
            metadata: {
              __typename: 'CardImageMetadata',
              img_position_x: null,
              img_position_y: null,
              zoom: null,
              background_color: null,
              editor_version: null,
              ...sanitizedInput.metadata,
            },
          },
          __typename: 'UpdateCardImagePayload',
        },
      },
    });
    return data?.updateCardImage?.image;
  };

  const updateOrCreate = async (
    input: CardImageInput,
    type: keyof CardImages,
    cardAttributeType?: CardAttributeType,
  ) => {
    const { id, original_url, edited_url } = input;
    const url = original_url || edited_url;

    const attributeTypeId = getAttributeId(type);

    if (!attributeTypeId) {
      return;
    }
    if (!id) {
      return create({ ...input, original_url: url ?? '', attribute_type_id: attributeTypeId }, type);
    }

    return update({ ...input, id }, cardAttributeType);
  };

  const remove = async (id: string, card_id: string, imageName: string | null | undefined) => {
    const { data } = await removeImage({
      variables: { input: { id, card_id } },
      context: mutationContext,
      optimisticResponse: { removeCardImage: { id } },
      update: (cache) => {
        if (!imageName) {
          return;
        }

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: card_id }),
          fields: {
            // Remove the deleted image from the cache via the card object.
            images(existingCardImages) {
              return {
                ...existingCardImages,
                [imageName]: null,
              };
            },
          },
        });

        // This cleans up the de-referenced card field from the cache
        cache.gc();
      },
      // update: removeFromCache({ id, __typename: `CardImage` }),
    });
    return data?.removeCardImage?.id;
  };

  return { updateOrCreate, create, update, remove };
}
